'use client';

import { useCallback, useMemo, useState, type FormEvent } from 'react';

import { SearchCombobox, type SearchComboboxConfig } from '@/shared/components/search-combobox';

import type { SearchBarWidgetProps, SearchSuggestionItem } from '../types/search-bar';
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';

const DEFAULT_PLACEHOLDER = '¿Qué quieres aprender?';
const DEFAULT_RESULTS_URL = '/?s=';
const DEFAULT_EMPTY_LABEL = 'No encontramos coincidencias todavía.';

export function SearchBarWidget({
  autoFocus,
  className,
  debounceMs,
  dropdownPlacement = 'portal',
  emptyStateLabel = DEFAULT_EMPTY_LABEL,
  inputLabel,
  maxResults,
  minLength,
  onSelect,
  onSubmit,
  placeholder = DEFAULT_PLACEHOLDER,
  resultsUrl = DEFAULT_RESULTS_URL,
  resultsUrlSuffix = '',
  suggestUrl,
}: SearchBarWidgetProps) {
  const [query, setQuery] = useState('');

  const { items, isLoading } = useSearchSuggestions(query, {
    debounceMs,
    maxResults,
    minLength,
    suggestUrl,
  });

  const navigateToResults = useCallback(
    (finalQuery: string) => {
      if (typeof window === 'undefined') return;
      window.location.assign(`${resultsUrl}${encodeURIComponent(finalQuery)}${resultsUrlSuffix}`);
    },
    [resultsUrl, resultsUrlSuffix],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>, nextQuery?: string) => {
      event.preventDefault();
      const finalQuery = (nextQuery ?? query).trim();
      if (!finalQuery) return;

      if (onSubmit) {
        onSubmit(finalQuery);
        return;
      }
      navigateToResults(finalQuery);
    },
    [navigateToResults, onSubmit, query],
  );

  const handleSelect = useCallback(
    (item: SearchSuggestionItem) => {
      if (onSelect) {
        onSelect(item);
        return;
      }
      if (item.href && typeof window !== 'undefined') {
        window.location.assign(item.href);
      }
    },
    [onSelect],
  );

  const search = useMemo<SearchComboboxConfig>(
    () => ({
      emptyStateLabel,
      isLoading,
      label: inputLabel,
      onPreviewSelect: handleSelect,
      onQueryChange: setQuery,
      onSubmit: handleSubmit,
      placeholder,
      previewEnabled: true,
      previewItems: items,
      query,
    }),
    [emptyStateLabel, handleSelect, handleSubmit, inputLabel, isLoading, items, placeholder, query],
  );

  return (
    <SearchCombobox
      autoFocus={autoFocus}
      className={className}
      dropdownPlacement={dropdownPlacement}
      search={search}
    />
  );
}

export { SearchBarWidget as SearchBar };
export default SearchBarWidget;
